import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, ShieldCheck } from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";

// Public doctors directory — SEO internal-linking + sitemap seed.
export default function DoctorsDirectory() {
    const [q, setQ] = useState("");
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const pageSize = 24;

    async function load(nextPage = page, query = q) {
        setLoading(true);
        try {
            const r = await api.get("/public/doctors", { params: { page: nextPage, page_size: pageSize, q: query || undefined } });
            setRows(r.data.doctors || []);
            setTotal(r.data.total || 0);
            setPage(nextPage);
        } catch (e) { /* noop */ }
        finally { setLoading(false); }
    }
    useEffect(() => { load(1, ""); }, []);

    const pages = Math.max(1, Math.ceil(total / pageSize));

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <MarketingNav />
            <main className="flex-1 max-w-7xl w-full mx-auto px-6 py-16">
                <div className="text-xs uppercase tracking-widest text-[#2563EB] font-semibold">Verified doctors</div>
                <h1 className="mt-3 text-3xl lg:text-5xl font-bold tracking-tight text-[#123B5D]" data-testid="directory-heading">
                    Browse published doctor identities.
                </h1>
                <p className="mt-4 text-[#17212B]/70 max-w-2xl">
                    Every profile here has passed admin review and doctor approval, is grounded on facts the doctor provided, and shows their medical registration number.
                </p>

                <form onSubmit={(e) => { e.preventDefault(); load(1, q); }} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-2xl">
                    <div className="relative flex-1">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#17212B]/40" />
                        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, specialty, city…" className="pl-9" data-testid="directory-search" />
                    </div>
                    <Button type="submit" data-testid="directory-search-btn">Search</Button>
                </form>

                <div className="mt-6 text-sm text-[#17212B]/60">
                    {loading ? "Loading…" : `${total} ${total === 1 ? "doctor" : "doctors"} published`}
                </div>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="directory-grid">
                    {rows.map((d) => (
                        <Link key={d.id} to={`/dr/${d.slug}`} className="ddi-card p-5 hover:border-[#2563EB]/40 transition-colors" data-testid={`directory-card-${d.slug}`}>
                            <div className="flex items-start gap-3">
                                {d.profile_photo_url ? (
                                    <img src={d.profile_photo_url} alt={d.full_name} className="h-14 w-14 rounded-full object-cover ring-2 ring-[#2563EB]/20" />
                                ) : (
                                    <div className="h-14 w-14 rounded-full bg-[#123B5D] text-white font-semibold flex items-center justify-center text-lg">
                                        {(d.full_name || "?").replace(/^Dr\.?\s*/, "").split(" ").map((s) => s[0]).slice(0, 2).join("")}
                                    </div>
                                )}
                                <div className="min-w-0">
                                    <div className="font-bold text-[#123B5D] truncate">{d.full_name}</div>
                                    <div className="text-xs text-[#17212B]/70">{d.specialization}</div>
                                    <div className="text-xs text-[#17212B]/50 mt-1 flex items-center gap-1"><MapPin size={11} />{[d.city, d.state].filter(Boolean).join(", ")}</div>
                                </div>
                            </div>
                            {d.medical_registration_number && (
                                <div className="mt-4 pt-3 border-t border-[#123B5D]/10 flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#0F766E]">
                                    <ShieldCheck size={11} /> Reg: {d.medical_registration_number}
                                </div>
                            )}
                        </Link>
                    ))}
                </div>

                {pages > 1 && (
                    <div className="mt-8 flex items-center justify-center gap-2">
                        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => load(page - 1)} data-testid="directory-prev">Prev</Button>
                        <span className="text-sm text-[#17212B]/60">Page {page} / {pages}</span>
                        <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => load(page + 1)} data-testid="directory-next">Next</Button>
                    </div>
                )}
            </main>
            <MarketingFooter />
        </div>
    );
}
